/* Product page: the picture row, the large view and the phone buy bar, each its own element, plus the price line,
   which follows the form. The form itself is <product-form> (product-form.js), shared with quick add. */
const { openPanel, openQuick, besidePicture } = window.Laley;
const mouse = matchMedia('(hover: hover) and (pointer: fine)');
const wide = matchMedia('(min-width: 800px)');

/* <media-gallery>: arrows, mouse drag and a quiet edge hover move the picture row; a click opens the large view.
   A colour chosen in the form brings its own picture to the front (the variant's picture). Closing the large view
   leaves the row, and the focus, on the picture last seen there. */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery]');
    this.count = this.querySelector('[data-gallery-index]');
    this.steps = this.querySelectorAll('[data-gallery-step]');
    this.dragEvents = new AbortController();
    const options = { signal: this.dragEvents.signal };
    this.track.addEventListener('scroll', () => this.place(), { ...options, passive: true });
    this.addEventListener('click', this, options);
    for (const type of [
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel',
      'lostpointercapture',
      'pointerleave',
      'dragstart',
    ])
      this.track.addEventListener(type, this, options);
    // Catch the compatibility click before it reaches the zoom button's gallery handler.
    this.track.addEventListener('click', this, { ...options, capture: true });
    window.addEventListener('blur', this, options);
    window.addEventListener('resize', this, options);
    document.addEventListener('visibilitychange', this, options);
    this.edgeMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const stopEdge = () => this.stopEdge();
    this.track.addEventListener('wheel', stopEdge, { ...options, passive: true });
    document.addEventListener('keydown', stopEdge, options);
    document.addEventListener('panel:open', stopEdge, options);
    // Page movement invalidates the cached edge bounds. The gallery's own scrolling does not.
    document.addEventListener(
      'scroll',
      (event) => {
        if (event.target !== this.track) this.stopEdge();
      },
      { ...options, capture: true, passive: true },
    );
    for (const preference of [mouse, wide, this.edgeMotion]) preference.addEventListener('change', stopEdge, options);
    this.suppressClick = false;
    this.closest('[data-product-page]')?.addEventListener(
      'variant:change',
      ({ detail: { media } }) => {
        if (!media || media === this.media) return;
        this.stopEdge();
        this.media = media;
        this.show(this.track.querySelector(`[data-media-id="${media}"]`));
      },
      options,
    );
    document.getElementById('panel').addEventListener(
      'close',
      () => {
        this.syncVideos();
        if (!this.viewer) return;
        const shot = this.shots[this.viewer.index]?.closest('.gallery-shot');
        this.viewer = null;
        this.show(shot);
        shot?.querySelector('[data-zoom]').focus({ preventScroll: true });
      },
      options,
    );
    this.videos = [...this.querySelectorAll('.gallery-video')];
    if (this.videos.length) {
      // Visible desktop films autoplay too. On mobile, only the active frame can start, never an adjacent glimpse.
      // A compact Play button remains available if autoplay is declined by the browser or visitor preferences.
      this.videoMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.videoInView = new Map();
      this.videoState = new Map(this.videos.map((video) => [video, { tried: false, userPaused: false }]));
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
            this.videoInView.set(target, isIntersecting && intersectionRatio >= 0.5);
          });
          this.syncVideos();
        },
        { threshold: [0, 0.5] },
      );
      this.onVideoContextChange = () => this.syncVideos();
      this.onVideoPlay = ({ target }) => {
        if (!this.videoCanPlay(target)) return this.pauseVideo(target);
        const state = this.videoState.get(target);
        state.userPaused = false;
        state.tried = true;
        this.reflectVideo(target);
      };
      this.onVideoPause = ({ target }) => {
        const state = this.videoState.get(target);
        const automatic = state.galleryPause;
        state.galleryPause = false;
        if (!target.paused) return;
        state.automatic = false;
        if (!automatic && this.videoCanPlay(target)) {
          state.userPaused = true;
          target.autoplay = false;
        }
        this.reflectVideo(target);
      };
      this.videos.forEach((video) => {
        this.videoObserver.observe(video);
        video.addEventListener('play', this.onVideoPlay, options);
        video.addEventListener('pause', this.onVideoPause, options);
        const toggle = video.closest('.gallery-shot').querySelector('[data-video-toggle]');
        if (toggle) {
          video.controls = false;
          toggle.hidden = false;
          this.reflectVideo(video);
        }
      });
      document.addEventListener('visibilitychange', this.onVideoContextChange, options);
      document.addEventListener('panel:open', this.onVideoContextChange, options);
      wide.addEventListener('change', this.onVideoContextChange, options);
      this.videoMotion.addEventListener('change', this.onVideoContextChange, options);
      navigator.connection?.addEventListener('change', this.onVideoContextChange, options);
    }
    this.place();
  }

  disconnectedCallback() {
    this.stopEdge();
    this.endDrag();
    this.dragEvents?.abort();
    this.videoObserver?.disconnect();
    this.videos?.forEach((video) => this.pauseVideo(video));
  }

  videoCanPlay(video) {
    return (
      this.isConnected &&
      !document.hidden &&
      !document.getElementById('panel').open &&
      this.videoInView.get(video) === true &&
      (wide.matches || video.closest('.gallery-shot') === this.activeFrame)
    );
  }

  pauseVideo(video) {
    const state = this.videoState.get(video);
    state.request = (state.request || 0) + 1;
    state.automatic = false;
    if (video.paused) return;
    state.galleryPause = true;
    video.pause();
  }

  reflectVideo(video) {
    const toggle = video.closest('.gallery-shot')?.querySelector('[data-video-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-label', video.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    toggle.querySelector('[data-video-play]').hidden = !video.paused;
    toggle.querySelector('[data-video-pause]').hidden = video.paused;
  }

  async playVideo(video, automatic = true) {
    if (!this.videoCanPlay(video)) return;
    const state = this.videoState.get(video);
    const request = (state.request = (state.request || 0) + 1);
    state.tried = true;
    state.automatic = automatic;
    video.defaultMuted = video.muted = true;
    video.playsInline = true;
    video.autoplay = automatic;
    try {
      await video.play();
      // A swipe or panel can finish before the asynchronous playback request does.
      if (state.request !== request) return;
      if (!this.videoCanPlay(video)) this.pauseVideo(video);
    } catch {
      if (state.request !== request) return;
      // One attempt per activation; explicit Play remains usable, including in iOS Low Power Mode.
      state.automatic = false;
      video.autoplay = false;
    }
    this.reflectVideo(video);
  }

  syncVideos() {
    this.videos?.forEach((video) => {
      const state = this.videoState.get(video);
      const auto =
        !this.videoMotion.matches && !navigator.connection?.saveData && this.videoCanPlay(video) && !state.userPaused;
      if (!auto) {
        video.autoplay = false;
        state.tried = false;
      }
      if (!this.videoCanPlay(video) || (state.automatic && !auto)) this.pauseVideo(video);
      if (auto && !state.tried) this.playVideo(video);
      this.reflectVideo(video);
    });
  }

  /** The row scrolls to a picture, at once. */
  show(shot) {
    if (!shot) return;
    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    const left = shot.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    this.track.scrollTo({ left: this.track.scrollLeft + left - padding, behavior: 'instant' });
  }

  get shots() {
    return [...this.querySelectorAll('[data-zoom]')];
  }

  get frames() {
    return this.track.querySelectorAll('.gallery-shot');
  }

  edgeCanMove() {
    return (
      this.isConnected &&
      mouse.matches &&
      wide.matches &&
      !this.edgeMotion.matches &&
      !document.hidden &&
      !document.getElementById('panel').open &&
      !this.drag
    );
  }

  moveEdge(event) {
    if (event.pointerType !== 'mouse' || event.buttons || !this.edgeCanMove() || !event.target.closest('[data-zoom]'))
      return this.stopEdge(false);
    const box = (this.edgeBox ||= this.track.getBoundingClientRect());
    const zone = Math.min(64, box.width * 0.12);
    const direction = event.clientX >= box.right - zone ? 1 : event.clientX <= box.left + zone ? -1 : 0;
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (
      !direction ||
      (direction < 0 && this.track.scrollLeft <= 0) ||
      (direction > 0 && this.track.scrollLeft >= max - 1)
    )
      return this.stopEdge(false);
    if (direction === this.edgeDirection) return;
    this.stopEdge(false);
    this.edgeDirection = direction;
    this.edgeMax = max;
    // A brief pass across an edge must not start the row moving.
    this.edgeTimer = setTimeout(() => {
      this.edgeTimer = 0;
      if (!this.edgeCanMove()) return this.stopEdge();
      this.track.classList.add('is-hover-scrolling');
      this.edgeLeft = this.track.scrollLeft;
      this.edgeTime = 0;
      this.edgeFrame = requestAnimationFrame((time) => this.panEdge(time));
    }, 240);
  }

  panEdge(time) {
    this.edgeFrame = 0;
    if (!this.edgeCanMove() || !this.edgeDirection) return this.stopEdge();
    const elapsed = this.edgeTime ? Math.min(time - this.edgeTime, 32) : 0;
    this.edgeTime = time;
    // Keep subpixels in our own position so rounding cannot change the speed at different refresh rates.
    this.edgeLeft = Math.max(0, Math.min(this.edgeMax, this.edgeLeft + this.edgeDirection * elapsed * 0.1));
    this.track.scrollLeft = this.edgeLeft;
    if ((this.edgeDirection < 0 && this.edgeLeft <= 0) || (this.edgeDirection > 0 && this.edgeLeft >= this.edgeMax))
      return this.stopEdge();
    this.edgeFrame = requestAnimationFrame((next) => this.panEdge(next));
  }

  stopEdge(restoreSnap = true) {
    clearTimeout(this.edgeTimer);
    cancelAnimationFrame(this.edgeFrame);
    cancelAnimationFrame(this.edgeRelease);
    this.edgeTimer = this.edgeFrame = this.edgeRelease = this.edgeDirection = 0;
    if (restoreSnap) {
      this.edgeBox = null;
      this.track?.classList.remove('is-hover-scrolling');
    }
  }

  startDrag(event) {
    this.endDrag();
    // A new gesture clears any click guard left by a release outside the browser.
    this.suppressClick = false;
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false) return;
    // Native video controls, including their shadow DOM, keep their own pointer handling.
    if (!event.target.closest('[data-zoom]')) return;
    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: this.track.scrollLeft,
      moved: false,
    };
  }

  moveDrag(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    if (!(event.buttons & 1)) return this.endDrag(event);
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) <= 6) return;
      this.suppressClick = true;
      // A vertical gesture is not a gallery slide or a zoom click.
      if (Math.abs(dx) <= Math.abs(dy)) {
        this.drag = null;
        return;
      }
      drag.moved = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(drag.id);
    }
    event.preventDefault();
    this.track.scrollLeft = drag.left - dx;
  }

  endDrag(event) {
    const drag = this.drag;
    if (!drag || (event?.pointerId !== undefined && event.pointerId !== drag.id)) return;
    this.drag = null;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(drag.id)) this.track.releasePointerCapture(drag.id);
    if (drag.moved && this.isConnected) {
      this.show(this.frames[Math.round(this.track.scrollLeft / this.pitch)]);
      this.place();
    }
  }

  handleEvent(event) {
    if (event.type === 'pointerdown') {
      // Freeze under the mouse before its click. Restoring snap here would move the intended zoom target.
      this.stopEdge(event.pointerType !== 'mouse');
      return this.startDrag(event);
    }
    if (event.type === 'pointermove') return this.drag ? this.moveDrag(event) : this.moveEdge(event);
    if (event.type === 'pointerup') {
      this.endDrag(event);
      // The capture click restores snap first; this also covers a release which produces no click.
      this.edgeRelease = requestAnimationFrame(() => this.stopEdge());
      return;
    }
    if (event.type === 'pointerleave') {
      // Before the drag threshold there is no capture, so an outside release may never reach the track.
      if (this.drag && !this.drag.moved) this.endDrag();
      this.stopEdge(!this.drag);
      return;
    }
    if (['pointercancel', 'lostpointercapture', 'blur', 'resize'].includes(event.type)) {
      this.stopEdge();
      return this.endDrag(event);
    }
    if (event.type === 'visibilitychange') {
      if (document.hidden) {
        this.stopEdge();
        this.endDrag();
      }
      return;
    }
    if (event.type === 'dragstart') {
      if (event.target.closest('[data-zoom]')) event.preventDefault();
      return;
    }
    if (event.currentTarget === this.track) {
      this.stopEdge();
      if (this.suppressClick && event.detail > 0) {
        this.suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }
    const toggle = event.target.closest('[data-video-toggle]');
    if (toggle) {
      const video = toggle.closest('.gallery-shot').querySelector('.gallery-video');
      const state = this.videoState.get(video);
      if (video.paused) {
        state.userPaused = false;
        this.playVideo(video, false);
      } else {
        state.userPaused = true;
        state.automatic = false;
        video.autoplay = false;
        video.pause();
      }
      return;
    }
    const step = event.target.closest('[data-gallery-step]');
    if (step) {
      this.stopEdge();
      this.track.scrollBy({ left: Number(step.dataset.galleryStep) * this.pitch, behavior: 'smooth' });
      return;
    }
    const zoom = event.target.closest('[data-zoom]');
    if (!zoom) return;
    openPanel('viewer', { side: 'full', trigger: zoom });
    const list = this.shots.map((shot) => {
      const image = shot.querySelector('img');
      return {
        src: shot.dataset.full,
        alt: image?.alt || '',
        width: Number(shot.dataset.width || image?.width),
        height: Number(shot.dataset.height || image?.height),
        // Only a decoded gallery picture can be the instant preview. An unseen lazy image must not trigger a
        // second, oversized gallery request alongside the viewer's correctly sized file.
        preview: image?.complete && image.naturalWidth ? image.currentSrc || image.src : '',
      };
    });
    this.viewer = document.querySelector('#panel-body image-viewer');
    this.viewer?.show(list, Number(zoom.dataset.zoom));
  }

  /** One step of the row: a picture and the gap after it. */
  get pitch() {
    return this.track.querySelector('.gallery-shot').offsetWidth + parseFloat(getComputedStyle(this.track).columnGap);
  }

  place() {
    const overflow = this.track.scrollWidth - this.track.clientWidth;
    const end = this.track.scrollLeft >= overflow - 1;
    const index = overflow > 1 && end ? this.frames.length - 1 : Math.round(this.track.scrollLeft / this.pitch);
    this.activeFrame = this.frames[index];
    if (this.count && this.count.textContent !== String(index + 1)) this.count.textContent = index + 1;
    this.steps.forEach((step) =>
      step.setAttribute('aria-disabled', step.dataset.galleryStep < 0 ? this.track.scrollLeft <= 1 : end),
    );
    this.syncVideos();
  }
}

/* <image-viewer>: show the decoded gallery picture immediately, then replace it only when a sharper file is
   decoded. Fit-sized neighbours are warmed while open; the full resolution belongs to an actual zoom. */
class ImageViewer extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('[data-viewer-image]');
    this.img = this.image.querySelector('img');
    this.img.draggable = false;
    this.status = this.querySelector('[data-viewer-status]');
    this.events = new AbortController();
    this.files = new Map();
    this.pictures = new Map();
    this.request = 0;
    this.origin = { x: 50, y: 50 };
    this.touches = new Map();
    this.scale = parseFloat(getComputedStyle(this).getPropertyValue('--viewer-zoom')) || 2.2;
    this.zoomScale = 1;
    this.maxScale = Math.max(4, this.scale);
    const options = { signal: this.events.signal };
    this.addEventListener('click', this, options);
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'lostpointercapture'])
      this.image.addEventListener(type, this, options);
    // Safari's legacy gesture events must stay inside the photo too; the page and its controls never scale.
    for (const type of ['gesturestart', 'gesturechange', 'gestureend'])
      this.image.addEventListener(type, this, { ...options, passive: false });
    document.addEventListener('keydown', this, options);
    window.addEventListener('resize', this, options);
  }

  disconnectedCallback() {
    this.events.abort();
    this.request++;
    this.cancelPan();
    this.cancelGrip();
    this.writeView(1, { x: 50, y: 50 });
    cancelAnimationFrame(this.resizeFrame);
    this.files.clear();
    this.pictures.clear();
  }

  show(list, index) {
    this.list = list;
    this.go(index);
  }

  go(index) {
    this.request++;
    this.index = (index + this.list.length) % this.list.length;
    this.zoom(false);
    const entry = this.list[this.index];
    this.img.alt = entry.alt;
    this.layout();
    const saved = this.pictures.get(this.index);
    const previewWidth = entry.preview ? this.sourceWidth(entry.preview, entry) : 0;
    const src = saved?.width >= previewWidth ? saved.src : entry.preview;
    this.pixels = Math.max(saved?.width || 0, previewWidth);
    this.img.style.visibility = src ? '' : 'hidden';
    if (src) this.img.src = src;
    else this.img.removeAttribute('src');
    this.querySelector('[data-viewer-index]').textContent = this.index + 1;
    this.upgrade();
  }

  sourceWidth(src, entry) {
    return Math.min(Number(new URL(src, location.href).searchParams.get('width')) || entry.width, entry.width);
  }

  /** Untransformed bounds, shared with the cursor. Neither pan nor pointer movement measures the transformed image. */
  layout() {
    const entry = this.list?.[this.index];
    if (!entry) return null;
    const box = this.image.getBoundingClientRect();
    this.frameBounds = box;
    const ratio = entry.width / entry.height;
    const width = Math.min(box.width, box.height * ratio);
    const height = width / ratio;
    const left = box.left + (box.width - width) / 2;
    const top = box.top + (box.height - height) / 2;
    this.img.style.width = `${width}px`;
    this.img.style.height = `${height}px`;
    this.bounds = { left, top, width, height, right: left + width, bottom: top + height };
    return this.bounds;
  }

  get pictureBounds() {
    return this.bounds || this.layout();
  }

  file(index, zoom = false) {
    const entry = this.list[index];
    const box = this.frameBounds;
    const fit = Math.min(box.width, box.height * (entry.width / entry.height));
    const needed = Math.ceil(fit * devicePixelRatio * (zoom ? Math.max(this.scale, this.zoomScale) : 1));
    const width = Math.min(entry.width, [500, 750, 1000, 1400, 1800, 2400].find((size) => size >= needed) || 2400);
    const url = new URL(entry.src, location.href);
    url.searchParams.set('width', width);
    return { src: url.href, width };
  }

  load(file, priority = 'high') {
    if (this.files.has(file.src)) return this.files.get(file.src);
    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      image.onload = async () => {
        try {
          await image.decode();
          resolve({ src: file.src, width: image.naturalWidth });
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = () => reject(new Error('Viewer image unavailable'));
      image.src = file.src;
    });
    this.files.set(file.src, promise);
    promise.catch(() => this.files.delete(file.src));
    return promise;
  }

  message(state) {
    this.status.hidden = !state;
    this.status.querySelector('[role="status"]').textContent = state ? this.status.dataset[state] : '';
    this.status.querySelector('[data-viewer-retry]').hidden = state !== 'failed';
    this.image.setAttribute('aria-busy', state === 'loading');
  }

  async upgrade() {
    const index = this.index;
    const file = this.file(index, this.zoomed || this.grip?.mode === 'pinch');
    const request = ++this.request;
    if (this.pixels >= file.width) {
      this.message('');
      if (!this.zoomed) this.warm();
      return;
    }
    this.message(this.pixels ? '' : 'loading');
    try {
      const result = await this.load(file);
      if (!this.isConnected || request !== this.request) return;
      this.pictures.set(index, result);
      this.pixels = result.width;
      this.img.src = result.src;
      this.img.style.visibility = '';
      this.message('');
      if (!this.zoomed) this.warm();
    } catch {
      if (this.isConnected && request === this.request) this.message(this.pixels ? '' : 'failed');
    }
  }

  warm() {
    if (navigator.connection?.saveData || this.list.length < 2) return;
    const neighbours = new Set([
      (this.index + 1) % this.list.length,
      (this.index + this.list.length - 1) % this.list.length,
    ]);
    for (const index of neighbours) {
      const file = this.file(index);
      if (this.pictures.get(index)?.width >= file.width) continue;
      this.load(file, 'low').then(
        (result) => {
          if (this.isConnected && !(this.pictures.get(index)?.width >= result.width)) this.pictures.set(index, result);
        },
        () => {},
      );
    }
  }

  get zoomed() {
    return this.image.classList.contains('is-zoomed');
  }

  zoom(on, event) {
    this.cancelPan();
    const interrupted = !!this.grip || this.touches.size > 0;
    this.cancelGrip();
    this.moved = interrupted;
    if (on && !this.pixels) return;
    this.writeView(on ? this.scale : 1, { x: 50, y: 50 });
    if (event && on) this.aim(event, true);
    if (on) this.upgrade();
  }

  writeView(scale, origin) {
    this.zoomScale = Math.min(this.maxScale, Math.max(1, scale));
    this.image.style.setProperty('--viewer-scale', this.zoomScale);
    this.writeOrigin(origin);
    const on = this.zoomScale > 1;
    this.image.classList.toggle('is-zoomed', on);
    this.image.setAttribute('aria-label', on ? this.image.dataset.labelOut : this.image.dataset.labelIn);
  }

  writeOrigin(origin) {
    const r = this.pictureBounds;
    // Keep a letterboxed axis centred until the enlarged photo can fill it. Neither edge can be pulled into view.
    const limit = (size, frame) =>
      this.zoomScale > 1 ? (Math.max(0, (size * this.zoomScale - frame) / 2) / ((this.zoomScale - 1) * size)) * 100 : 0;
    const clamp = (value, range) => Math.min(50 + range, Math.max(50 - range, value));
    this.origin = {
      x: clamp(origin.x, r ? limit(r.width, this.frameBounds.width) : 0),
      y: clamp(origin.y, r ? limit(r.height, this.frameBounds.height) : 0),
    };
    this.img.style.transformOrigin = `${this.origin.x}% ${this.origin.y}%`;
  }

  cancelPan() {
    cancelAnimationFrame(this.panFrame);
    this.panFrame = null;
    this.pendingView = null;
  }

  flushPan() {
    const pending = this.pendingView;
    this.cancelPan();
    if (pending) this.writeView(pending.scale, pending.origin);
  }

  cancelGrip() {
    const captured = new Set(this.touches.keys());
    if (this.grip?.id != null) captured.add(this.grip.id);
    this.grip = null;
    this.touches.clear();
    this.image.classList.remove('is-manipulating');
    // Clear the gesture first: releasePointerCapture can synchronously dispatch lostpointercapture.
    for (const id of captured) if (this.image.hasPointerCapture(id)) this.image.releasePointerCapture(id);
  }

  queueView(scale, origin) {
    this.pendingView = { scale, origin };
    if (this.panFrame != null) return;
    this.panFrame = requestAnimationFrame(() => {
      const pending = this.pendingView;
      this.panFrame = null;
      this.pendingView = null;
      if (pending) this.writeView(pending.scale, pending.origin);
    });
  }

  queueOrigin(origin) {
    this.queueView(this.zoomScale, origin);
  }

  aim(event, immediate = false) {
    const r = this.pictureBounds;
    const origin = { x: ((event.clientX - r.left) / r.width) * 100, y: ((event.clientY - r.top) / r.height) * 100 };
    if (immediate) this.writeOrigin(origin);
    else this.queueOrigin(origin);
  }

  startGrip(pointer, afterPinch = false) {
    this.grip = {
      // A remaining finger may pan after a pinch, but cannot turn the same gesture into picture navigation.
      mode: this.zoomed ? 'pan' : afterPinch ? 'hold' : 'swipe',
      x: pointer.clientX,
      y: pointer.clientY,
      ox: this.origin.x,
      oy: this.origin.y,
      width: this.pictureBounds.width,
      height: this.pictureBounds.height,
      scale: this.zoomScale,
      id: pointer.pointerId,
      afterPinch,
    };
  }

  startPinch() {
    this.flushPan();
    const [a, b] = this.touches.values();
    const r = this.pictureBounds;
    const x = (a.clientX + b.clientX) / 2 - r.left;
    const y = (a.clientY + b.clientY) / 2 - r.top;
    this.grip = {
      mode: 'pinch',
      distance: Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)),
      scale: this.zoomScale,
      // The point beneath the two fingers stays beneath their midpoint as the distance and midpoint change.
      x: (x - (1 - this.zoomScale) * ((this.origin.x / 100) * r.width)) / this.zoomScale,
      y: (y - (1 - this.zoomScale) * ((this.origin.y / 100) * r.height)) / this.zoomScale,
    };
    this.moved = true;
    this.image.classList.add('is-manipulating');
    this.upgrade();
  }

  pinch() {
    const [a, b] = this.touches.values();
    const grip = this.grip;
    const r = this.pictureBounds;
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const scale = Math.min(this.maxScale, Math.max(1, (grip.scale * distance) / grip.distance));
    const origin = (midpoint, offset, anchor, size) =>
      scale > 1 ? (((midpoint - offset - scale * anchor) / (1 - scale)) * 100) / size : 50;
    this.queueView(scale, {
      x: origin((a.clientX + b.clientX) / 2, r.left, grip.x, r.width),
      y: origin((a.clientY + b.clientY) / 2, r.top, grip.y, r.height),
    });
  }

  handleEvent(event) {
    if (event.type === 'keydown') {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === '+' || event.key === '=') this.zoom(true);
      if (event.key === '-' || event.key === '0') this.zoom(false);
      if ((event.key === 'Enter' || event.key === ' ') && event.target === this.image) {
        event.preventDefault();
        this.zoom(!this.zoomed);
      }
      return;
    }
    if (event.type === 'resize') {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.flushPan();
        if (this.grip) this.moved = true;
        this.cancelGrip();
        this.layout();
        this.writeOrigin(this.origin);
        this.upgrade();
      });
      return;
    }
    if (event.type.startsWith('gesture')) {
      if (event.cancelable) event.preventDefault();
      return;
    }
    if (event.type === 'pointerdown') {
      const touch = event.pointerType === 'touch';
      if (!this.pixels) return;
      if (!touch && (this.zoomed || event.button !== 0 || event.isPrimary === false)) {
        if (!this.touches.size && event.button === 0 && event.isPrimary !== false) this.moved = false;
        return;
      }
      this.flushPan();
      if (touch) {
        const fresh = this.touches.size === 0;
        this.touches.set(event.pointerId, {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
        });
        this.image.setPointerCapture(event.pointerId);
        if (this.touches.size > 1) {
          if (this.touches.size === 2) this.startPinch();
          return;
        }
        if (fresh) this.moved = false;
      } else {
        this.moved = false;
        this.image.setPointerCapture(event.pointerId);
      }
      this.startGrip(event);
      return;
    }
    if (event.type === 'pointerup' || event.type === 'pointercancel' || event.type === 'lostpointercapture') {
      const tracked = this.touches.has(event.pointerId);
      const grip = this.grip;
      if (!tracked && (!grip || event.pointerId !== grip.id)) return;
      if (event.type !== 'pointerup') {
        this.flushPan();
        this.cancelGrip();
        this.moved = true;
        return;
      }
      if (tracked) {
        this.touches.set(event.pointerId, {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
        });
        if (grip?.mode === 'pinch') this.pinch();
      }
      this.flushPan();
      this.touches.delete(event.pointerId);
      if (grip?.mode === 'pinch') {
        this.grip = null;
        if (this.touches.size >= 2) this.startPinch();
        else if (this.touches.size) this.startGrip(this.touches.values().next().value, true);
        else this.image.classList.remove('is-manipulating');
        this.moved = true;
        if (this.zoomed) this.upgrade();
      } else if (grip && event.pointerId === grip.id) {
        this.grip = null;
        const dx = event.clientX - grip.x;
        const dy = event.clientY - grip.y;
        if (Math.hypot(dx, dy) > 8 || grip.afterPinch) this.moved = true;
        const distance = Math.max(40, Math.min(80, this.frameBounds.width * 0.12));
        if (grip.mode === 'swipe' && Math.abs(dx) >= distance && Math.abs(dx) > Math.abs(dy) * 1.3) {
          this.go(this.index + (dx < 0 ? 1 : -1));
          // go() resets the zoom gesture. Consume this swipe's subsequent click as well.
          this.moved = true;
        }
        this.image.classList.remove('is-manipulating');
      }
      if (this.image.hasPointerCapture(event.pointerId)) this.image.releasePointerCapture(event.pointerId);
      return;
    }
    if (event.type === 'pointermove') {
      if (event.pointerType === 'mouse' && this.zoomed && !this.touches.size) return this.aim(event);
      if (this.touches.has(event.pointerId)) {
        this.touches.set(event.pointerId, {
          pointerId: event.pointerId,
          clientX: event.clientX,
          clientY: event.clientY,
        });
      }
      const grip = this.grip;
      if (grip?.mode === 'pinch') {
        if (this.touches.has(event.pointerId)) this.pinch();
        return;
      }
      if (!grip || event.pointerId !== grip.id) return;
      const dx = event.clientX - grip.x;
      const dy = event.clientY - grip.y;
      if (Math.hypot(dx, dy) > 8) this.moved = true;
      if (grip.mode !== 'pan') return;
      this.image.classList.add('is-manipulating');
      const step = (distance, size) => (distance / ((grip.scale - 1) * size)) * 100;
      this.queueOrigin({ x: grip.ox - step(dx, grip.width), y: grip.oy - step(dy, grip.height) });
      return;
    }
    if (event.target.closest('[data-viewer-retry]')) {
      this.image.focus({ preventScroll: true });
      this.upgrade();
      return;
    }
    const step = event.target.closest('[data-viewer-step]');
    if (step) return this.go(this.index + Number(step.dataset.viewerStep));
    if (event.target.closest('[data-viewer-image]')) {
      if (this.moved) {
        this.moved = false;
        return;
      }
      const side = mouse.matches && !this.zoomed ? besidePicture(this.img, event) : 0;
      if (side) return this.go(this.index + side);
      this.zoom(!this.zoomed, event);
    }
  }
}

/* <sticky-buy>: the phone bar is the way to buy while the page's own button is out of sight, but it never covers a
   size or colour that is otherwise in reach (half of it showing), never while a field is typed in, and steps aside
   once the footer reaches the top of the bar. IntersectionObservers tell it; nothing is measured while
   scrolling. From 800 px it names the product and the choice in words. */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    const page =
      this.closest('.shopify-section')?.querySelector('[data-product-page]') ||
      document.querySelector('[data-product-page]');
    const pageForm = page?.querySelector('.pdp-info product-form');
    const siteHeader = document.querySelector('.section-header');
    this.button = this.querySelector('button');
    this.buy = pageForm?.querySelector('[data-buy]');
    if (!this.buy) return;
    // Preserve native validation and the actual opener; input[name=id] shadows the form.id property.
    let formId = pageForm.form.getAttribute('id');
    if (!formId) {
      formId = `StickyProductForm-${this.closest('.shopify-section')?.id || 'main'}`;
      pageForm.form.setAttribute('id', formId);
    }
    this.button.type = 'submit';
    this.button.setAttribute('form', formId);
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.seen = { button: false, footer: false, typing: false, reach: new Set(), strip: new Set() };
    // The form has spoken before this bar was defined; the bar is measured with its words in.
    const followForm = () =>
      this.follow(
        pageForm.state || {
          label: this.buy.querySelector('[data-buy-label]').textContent,
          mode: this.buy.dataset.mode,
          disabled: this.buy.disabled,
        },
      );
    followForm();
    pageForm.addEventListener('product:pending', followForm, options);
    const measure = () => {
      const hidden = this.hidden;
      this.hidden = false;
      this.height = this.offsetHeight;
      this.hidden = hidden;
      // Include the phone's browser/safe-area clearance when keeping choices and cookie controls in reach.
      document.documentElement.style.setProperty('--sticky-height', `${this.height}px`);
    };
    const watch = (options, fn) =>
      new IntersectionObserver((entries) => {
        entries.forEach(fn);
        this.update();
      }, options);
    const footer = document.querySelector('.site-footer');
    // Every choice the form shows, as V3 counts them (each label and colour of the decisions): sizes, colours, one-off
    // or subscription and its frequency, the recipient's box and fields.
    const controls = pageForm.querySelectorAll('label, .swatch, select');
    const watchStrip = () => {
      measure();
      this.footerWatch?.disconnect();
      if (footer) {
        // Percentage margins use viewport width; pixel clearance follows the actual bar on every screen.
        this.footerWatch = watch({ rootMargin: `0px 0px -${this.height}px 0px` }, (entry) => {
          this.seen.footer = entry.isIntersecting;
        });
        this.footerWatch.observe(footer);
      }
      // The notice changes height with the responsive menu; refresh its covered area on resize as well.
      this.buyWatch?.disconnect();
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--lly-header-height'));
      this.buyWatch = watch({ rootMargin: `-${header}px 0px 0px 0px`, threshold: 1 }, (entry) => {
        this.seen.button = entry.isIntersecting && entry.intersectionRatio >= 1;
      });
      this.buyWatch.observe(this.buy);
      this.reachWatch?.disconnect();
      this.seen.reach.clear();
      // Count only the choices visible above the bar, not choices hidden beneath its opaque surface.
      this.reachWatch = watch({ rootMargin: `-${header}px 0px -${this.height}px 0px`, threshold: 0.5 }, (entry) =>
        this.seen.reach[entry.isIntersecting && entry.intersectionRatio >= 0.5 ? 'add' : 'delete'](entry.target),
      );
      controls.forEach((control) => this.reachWatch.observe(control));
      this.strip?.disconnect();
      this.seen.strip.clear();
      this.strip = watch({ rootMargin: `-${Math.max(0, innerHeight - this.height)}px 0px 0px 0px` }, (entry) =>
        this.seen.strip[entry.isIntersecting ? 'add' : 'delete'](entry.target),
      );
      controls.forEach((control) => this.strip.observe(control));
    };
    watchStrip();
    addEventListener('resize', watchStrip, options);
    window.visualViewport?.addEventListener('resize', watchStrip, options);
    this.layoutWatch = new ResizeObserver(watchStrip);
    if (siteHeader) this.layoutWatch.observe(siteHeader);
    document.addEventListener(
      'focusin',
      (event) => {
        this.seen.typing = event.target.matches('input:not([type=radio]):not([type=checkbox]), textarea, select');
        this.update();
      },
      options,
    );
    document.addEventListener(
      'focusout',
      () => {
        this.seen.typing = false;
        this.update();
      },
      options,
    );
    document.getElementById('panel').addEventListener('close', () => {
      // A removed focused input need not emit focusout.
      this.seen.typing = false;
      this.update();
    }, options);
    document.addEventListener('panel:open', () => this.update(), options);
    page.addEventListener('variant:change', (event) => this.follow(event.detail), options);
    // Without a choice: on a phone quick add opens; with room for the column, the page goes back to the sizes.
    this.button.addEventListener(
      'click',
      (event) => {
        if (this.buy.dataset.mode !== 'choose') return;
        event.preventDefault();
        if (wide.matches || pageForm.form.querySelector('input[name="properties[_laley_early_access]"]')?.value)
          pageForm.pick();
        else openQuick(this.button.dataset.quickUrl, this.button);
      },
      options,
    );
    // Shopify refused an add made from the bar: the page's own line says why, so it comes into view over the page's
    // button, and the focus goes there (the bar then steps aside).
    pageForm.addEventListener(
      'cart:refused',
      ({ detail: { note } }) => {
        if (this.hidden || !note) return;
        note.scrollIntoView({ block: 'center' });
        this.buy.focus({ preventScroll: true });
      },
      options,
    );
  }

  disconnectedCallback() {
    this.events?.abort();
    for (const observer of [this.footerWatch, this.reachWatch, this.buyWatch, this.strip]) observer?.disconnect();
    this.layoutWatch?.disconnect();
  }

  follow({ label, mode, disabled, summary = '' }) {
    // Beside the name (from 800 px): what is chosen, in words (product-form.js).
    const busy = this.buy.hasAttribute('aria-busy');
    this.querySelector('[data-sticky-summary]').textContent = summary;
    this.button.querySelector('[data-buy-label]').textContent = busy
      ? this.buy.querySelector('[data-buy-label]').textContent
      : label;
    this.button.querySelector('[data-buy-price]').textContent = this.buy.querySelector('[data-buy-price]').textContent;
    this.button.dataset.mode = mode;
    this.button.formNoValidate = mode === 'notify';
    this.button.disabled = disabled || busy;
    if (busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
  }

  update() {
    const covers = [...this.seen.reach].some((control) => this.seen.strip.has(control));
    this.hidden =
      this.seen.button || covers || this.seen.footer || this.seen.typing || document.getElementById('panel').open;
  }
}

/* <size-guide>: «Vis mål for» shows the measurements of the chosen size. It opens on
   the size chosen on the page (V3), else on the first. */
class SizeGuide extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this);
    const pageForm = document.querySelector('[data-product-page] .pdp-info product-form');
    const chosen = pageForm?.data?.options[pageForm.index] || pageForm?.picked || [];
    const size = [...this.querySelectorAll('[data-guide-size]')].find((input) => chosen.includes(input.value));
    if (size && !size.checked) {
      size.checked = true;
      size.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this);
  }

  handleEvent(event) {
    if (!event.target.matches('[data-guide-size]')) return;
    this.querySelectorAll('[data-guide-row]').forEach((row) => {
      row.hidden = row.dataset.guideRow !== event.target.value;
    });
  }
}

/* Silent use films have one playback control; muted native playback is the no-JS and error fallback. */
class UsageGuide extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.clearWait();
    this.cancelFrame();
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.video = this.querySelector('video');
    this.poster = this.querySelector('[data-usage-poster]');
    this.controls = this.querySelector('[data-usage-controls]');
    this.playButton = this.querySelector('[data-usage-play]');
    this.playLabel = this.querySelector('[data-usage-play-label]');
    this.status = this.querySelector('[data-usage-status]');
    this.silence();
    this.video?.addEventListener('volumechange', () => this.silence(), options);
    this.request = (this.request || 0) + 1;
    this.pending = this.loading = this.failed = this.wantsPlayback = false;
    this.retry = this.hasFrame = this.cancelled = false;
    this.autoAttempted = false;
    this.enhanced = false;
    const panel = document.getElementById('panel');
    panel?.addEventListener('close', () => this.pause(), options);
    panel?.addEventListener('panel:open', () => this.startAutomatically(), options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.startAutomatically();
    }, options);
    window.addEventListener('pagehide', () => this.pause(), options);
    if (!this.video || !this.controls || !this.playButton || !this.playLabel || !this.status) return;
    this.playButton.addEventListener('click', () => this.toggle(), options);
    for (const type of ['play', 'playing', 'pause', 'ended', 'waiting', 'stalled']) {
      this.video.addEventListener(type, () => {
        // Fetch suspension is normal when playback still has buffered frames.
        if (type === 'stalled' && this.video.readyState >= 3) return;
        if (type === 'play' && this.cancelled && !this.wantsPlayback) {
          this.video.pause();
          return;
        }
        if ((type === 'pause' || type === 'ended') && (this.video.paused || this.video.ended)) {
          this.request++;
          this.pending = this.loading = this.wantsPlayback = false;
          this.clearWait();
          this.cancelFrame();
        } else if (type === 'playing') {
          this.pending = false;
          this.frameReady();
          this.loading = !this.hasFrame;
          if (this.hasFrame) this.clearWait();
        } else if ((type === 'waiting' || type === 'stalled') && this.wantsPlayback) {
          this.loading = true;
          this.armWait();
        }
        this.sync();
      }, options);
    }
    for (const type of ['loadeddata', 'canplay', 'timeupdate']) {
      this.video.addEventListener(type, () => this.frameReady(), options);
    }
    this.video.addEventListener('error', () => this.fallback(), options);
    this.enhanced = true;
    this.video.controls = false;
    this.video.tabIndex = -1;
    this.controls.hidden = false;
    if (this.poster) this.poster.hidden = false;
    this.sync();
    this.startAutomatically();
  }

  silence() {
    if (!this.video) return;
    if (!this.video.defaultMuted) this.video.defaultMuted = true;
    if (!this.video.muted) this.video.muted = true;
    if (this.video.volume !== 0) this.video.volume = 0;
  }

  active() {
    const panel = document.getElementById('panel');
    return this.isConnected && !document.hidden && panel?.open &&
      panel.dataset.view === 'info-use' && !('closing' in panel.dataset) && panel.contains(this);
  }

  startAutomatically() {
    if (!this.enhanced || this.failed || this.autoAttempted || !this.active() ||
      matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.autoAttempted = true;
    this.silence();
    this.video.playsInline = true;
    this.play(true);
  }

  clearWait() {
    clearTimeout(this.waitTimer);
    this.waitTimer = undefined;
  }

  armWait() {
    if (this.waitTimer !== undefined) return;
    this.waitTimer = setTimeout(() => {
      this.waitTimer = undefined;
      if (!this.isConnected || !this.wantsPlayback || this.failed) return;
      this.pause();
      this.retry = true;
      this.sync();
    }, 10000);
  }

  cancelFrame() {
    if (this.frameRequest !== undefined) this.video?.cancelVideoFrameCallback?.(this.frameRequest);
    this.frameRequest = undefined;
  }

  watchFrame(request) {
    this.cancelFrame();
    if (this.hasFrame || typeof this.video.requestVideoFrameCallback !== 'function') return;
    this.frameRequest = this.video.requestVideoFrameCallback(() => {
      this.frameRequest = undefined;
      if (request === this.request && this.isConnected && !this.events.signal.aborted) this.frameReady(true);
    });
  }

  frameReady(presented = false) {
    if (this.failed || this.hasFrame || !this.wantsPlayback || this.video.paused ||
      this.video.readyState < 2 || !this.video.videoWidth) return;
    // A real frame callback is strongest; advancing time also covers older or unreliable implementations.
    if (!presented && typeof this.video.requestVideoFrameCallback === 'function' && this.video.currentTime <= 0) return;
    this.hasFrame = true;
    this.pending = this.loading = false;
    this.clearWait();
    this.cancelFrame();
    if (this.poster) this.poster.hidden = true;
    this.sync();
  }

  toggle() {
    if (this.pending || (!this.video.paused && !this.video.ended)) return this.pause();
    this.play();
  }

  play(automatic = false) {
    const retry = this.retry;
    const request = ++this.request;
    const signal = this.events.signal;
    const video = this.video;
    this.silence();
    this.retry = this.cancelled = false;
    this.clearWait();
    this.pending = this.loading = this.wantsPlayback = true;
    this.armWait();
    this.sync();
    const failed = (error) => {
      if (signal.aborted || request !== this.request || !this.isConnected) return;
      if (error?.name === 'AbortError' || (automatic && error?.name === 'NotAllowedError')) this.pause();
      else this.fallback();
    };
    try {
      if (retry) {
        this.hasFrame = false;
        if (this.poster) this.poster.hidden = false;
        video.load();
      }
      if (video.ended) video.currentTime = 0;
      this.watchFrame(request);
      // Keep playback in the click activation for iPhone; a pending promise never removes the poster.
      Promise.resolve(video.play()).then(() => {
        if (signal.aborted || request !== this.request || !this.isConnected) {
          if (!this.isConnected || video !== this.video || !this.wantsPlayback) video.pause();
          return;
        }
        if (automatic && !this.active()) return this.pause();
        this.pending = false;
        this.frameReady();
        this.loading = !this.hasFrame;
        if (this.hasFrame) this.clearWait();
        this.sync();
      }, failed);
    } catch (error) {
      failed(error);
    }
  }

  sync() {
    if (!this.enhanced || this.failed) return;
    const state = this.pending || (this.loading && !this.hasFrame && this.wantsPlayback) ? 'cancel' :
      this.retry ? 'retry' : !this.video.paused && !this.video.ended ? 'pause' : this.video.ended ? 'replay' : 'play';
    const label = this.playButton.dataset[state] || this.playButton.dataset.play;
    this.playLabel.textContent = label;
    this.playButton.setAttribute('aria-label', label);
    const iconState = state === 'cancel' ? 'pause' : state === 'retry' ? 'play' : state;
    this.querySelectorAll('[data-usage-play-icon]').forEach((icon) => {
      icon.hidden = icon.dataset.usagePlayIcon !== iconState;
    });
    this.status.hidden = !(this.loading || this.retry);
    this.status.textContent = this.retry ? this.status.dataset.slow : this.loading ? this.status.dataset.loading : '';
  }

  fallback() {
    const restoreFocus = document.activeElement === this.playButton;
    this.silence();
    this.failed = true;
    this.pause();
    if (this.poster) this.poster.hidden = true;
    this.video.controls = true;
    this.video.tabIndex = 0;
    this.controls.hidden = true;
    this.status.textContent = this.status.dataset.failed;
    this.status.hidden = false;
    if (restoreFocus) this.video.focus({ preventScroll: true });
  }

  pause() {
    this.request = (this.request || 0) + 1;
    this.pending = this.loading = this.wantsPlayback = this.retry = false;
    this.cancelled = true;
    this.clearWait();
    this.cancelFrame();
    this.video?.pause();
    this.sync();
  }

  disconnectedCallback() {
    this.pause();
    this.events?.abort();
  }
}

/* The information views' tab bar, printed once (main-product.liquid): while one of them is open it stands above the
   panel's body (V3), so it stays as the view scrolls. It marks the view (a tab and its tabpanel), keeps the focus on
   the tab that opened it, and the arrow keys, Home and End open the next tab. */
const productSections = new WeakMap();
function mountProductSection(section) {
  if (productSections.has(section)) return;
  const page = section.querySelector('[data-product-page]');
  if (!page) return;
  const events = new AbortController();
  const options = { signal: events.signal };
  let leave = () => {};
  const tabsTemplate = section.querySelector('template[data-info-tabs]');
  if (tabsTemplate) {
    const panel = document.getElementById('panel');
    const body = document.getElementById('panel-body');
    const bar = tabsTemplate.content.firstElementChild.cloneNode(true);
    const tabs = [...bar.querySelectorAll('[role="tab"]')];
    let fromTab = false;
    leave = () => {
      bar.remove();
      ['role', 'aria-labelledby', 'tabindex'].forEach((name) => body.removeAttribute(name));
    };
    // Before the panel's own click handler opens the view.
    bar.addEventListener(
      'click',
      (event) => {
        fromTab = Boolean(event.target.closest('[role="tab"]'));
      },
      options,
    );
    bar.addEventListener(
      'keydown',
      (event) => {
        const index = tabs.indexOf(document.activeElement);
        const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 }[event.key];
        if (index < 0 || next === undefined) return;
        event.preventDefault();
        tabs[(next + tabs.length) % tabs.length].click();
      },
      options,
    );
    panel.addEventListener(
      'panel:open',
      ({ detail: { view } }) => {
        const tab = tabs.find((button) => button.dataset.panelOpen === view);
        if (!tab) return leave();
        if (!bar.isConnected) body.before(bar);
        tabs.forEach((button) => {
          button.setAttribute('aria-selected', String(button === tab));
          button.tabIndex = button === tab ? 0 : -1;
        });
        body.setAttribute('role', 'tabpanel');
        body.setAttribute('aria-labelledby', tab.id);
        body.tabIndex = 0;
        if (fromTab) {
          tab.focus({ preventScroll: true });
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        fromTab = false;
      },
      options,
    );
    panel.addEventListener('close', leave, options);
  }

  const info = page.querySelector('.pdp-info');
  let observer;
  if (info) {
    const rest = () => {
      const root = getComputedStyle(document.documentElement);
      const header = parseFloat(root.getPropertyValue('--lly-header-height'));
      const space = parseFloat(root.getPropertyValue('--lly-space-4'));
      page.style.setProperty('--info-top', `${Math.min(header + space, innerHeight - info.offsetHeight - space)}px`);
    };
    observer = new ResizeObserver(rest);
    observer.observe(info);
    addEventListener('resize', rest, options);
  }
  productSections.set(section, () => {
    events.abort();
    observer?.disconnect();
    leave();
    productSections.delete(section);
  });
}
const productRoots = (root) =>
  [...root.querySelectorAll('[data-product-page]')].map(
    (page) => page.closest('.shopify-section') || page.parentElement,
  );
productRoots(document).forEach(mountProductSection);
document.addEventListener('shopify:section:load', ({ target }) => productRoots(target).forEach(mountProductSection));
document.addEventListener('shopify:section:unload', ({ target }) =>
  productRoots(target).forEach((section) => productSections.get(section)?.()),
);

customElements.define('usage-guide', UsageGuide);
customElements.define('media-gallery', MediaGallery);
customElements.define('size-guide', SizeGuide);
customElements.define('image-viewer', ImageViewer);
customElements.define('sticky-buy', StickyBuy);

/* The price line follows the chosen variant: the price alone, or on sale the price and the price before with the
   words a screen reader hears first («Førpris»), as snippets/price.liquid prints them. */
document.addEventListener('variant:change', (event) => {
  const page = event.target.closest('[data-product-page]');
  const { price, compare, text, before } = event.detail;
  if (!page) return;
  // Only the main form may update this product's headline price.
  if (event.target !== page.querySelector('.pdp-info product-form')) return;
  const line = page.querySelector('[data-price-line]');
  if (!line) return;
  const sale = compare > price;
  line.classList.toggle('price--sale', sale);
  if (!sale) return line.replaceChildren(text);
  const del = document.createElement('del');
  del.append(
    Object.assign(document.createElement('span'), { className: 'sr-only', textContent: `${line.dataset.before} ` }),
    before,
  );
  line.replaceChildren(Object.assign(document.createElement('span'), { textContent: text }), del);
});

/* <product-reviews>: the reviews straight from Klaviyo, with the theme's public key and the endpoint its own reviews
   widget reads (one product and its colours, a page at a time). On the page the top ones (five stars with words),
   fetched when they come near; in the panel how many gave each number of stars and every review, eight at a time,
   highest first, then newest within a rating. Responses are shared for this page visit, including the full fit summary used by the
   size guide. Without an answer the reviews stay out of sight and the rating line still tells. */
const REVIEWS = 'https://fast.a.klaviyo.com/reviews/api/client_reviews';
// Klaviyo's order: 0 newest, 1 highest (newest first within a rating), 2 lowest. The page and the panel use 1,
// so a poor review never leads only for being the newest (Sondre).
const BEST = 1;
const reviewPages = new Map();
const reviewSummaries = new Map();
const reviewKey = ({ key, product }) => JSON.stringify([key, product]);

function fetchReviews({ key, product }, { sort = BEST, limit, offset = 0 }) {
  const query = new URLSearchParams({ product_id: product, company_id: key, type: 'reviews', sort, limit, offset });
  const url = `${REVIEWS}/${product}/?${query}`;
  if (reviewPages.has(url)) return reviewPages.get(url);
  const request = fetch(url).then((response) => {
    if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
    return response.json();
  });
  reviewPages.set(url, request);
  request.catch(() => reviewPages.delete(url));
  const id = reviewKey({ key, product });
  if (!reviewSummaries.has(id)) {
    const summary = request.then((data) => data.summary);
    reviewSummaries.set(id, summary);
    summary.catch(() => {
      if (reviewSummaries.get(id) === summary) reviewSummaries.delete(id);
    });
  }
  return request;
}

/* <review-fit>: Klaviyo's complete question aggregate, not a sample of the reviews on a page. The native widget
   maps its centered_range from 0–4 to 0–100%. Laley's existing verdict divides that range into three equal bands. */
class ReviewFit extends HTMLElement {
  async connectedCallback() {
    const { key, product, question } = this.dataset;
    if (!key || !product || !question) return;
    try {
      const id = reviewKey(this.dataset);
      if (!reviewSummaries.has(id)) fetchReviews(this.dataset, { limit: 1 });
      const summary = await reviewSummaries.get(id);
      const answer = summary?.question_answers?.[question];
      if (!this.isConnected || answer?.type !== 'centered_range' || answer.hidden === true) return;
      const score = answer.answer;
      const labels = ['min', 'center', 'max'].map((name) => answer.values?.[name]);
      if (
        typeof score !== 'number' ||
        !Number.isFinite(score) ||
        score < 0 ||
        score > 4 ||
        labels.some((label) => typeof label !== 'string' || !label.trim())
      )
        return;
      const position = score / 4;
      const verdict = labels[Math.min(2, Math.floor(position * 3))];
      this.querySelector('[data-fit-verdict]').textContent = verdict;
      this.style.setProperty('--fit-position', `${position * 100}%`);
      this.querySelector('[data-fit-scale]').setAttribute('aria-label', `${this.dataset.scaleLabel}: ${verdict}`);
      this.hidden = false;
    } catch {
      // The size guide keeps its garment/model advice; never invent a customer verdict when data is unavailable.
    }
  }
}
customElements.define('review-fit', ReviewFit);

class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.request = (this.request || 0) + 1;
    this.loading = false;
    this.list = this.querySelector('.review-list');
    this.template = this.querySelector('template[data-review]');
    this.offset = 0;
    if (this.dataset.mode === 'top') {
      this.near = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.near.disconnect();
          this.top();
        },
        { rootMargin: '600px 0px' },
      );
      this.near.observe(this.parentElement);
      return;
    }
    this.addEventListener('click', this);
    this.load();
  }

  disconnectedCallback() {
    this.request++;
    this.near?.disconnect();
  }

  handleEvent(event) {
    if (event.type === 'click' && event.target.closest('[data-review-more]')) this.load(true);
  }

  async fetchPage(limit) {
    return fetchReviews(this.dataset, { limit, offset: this.offset });
  }

  item(review) {
    const item = this.template.content.firstElementChild.cloneNode(true);
    const stars = item.querySelector('[data-review-stars]');
    stars.style.setProperty('--rating', review.rating);
    stars.setAttribute('aria-label', this.dataset.starsLabel.replace('#', review.rating));
    const date = item.querySelector('[data-review-date]');
    date.dateTime = review.created_at.slice(0, 10);
    date.textContent = new Date(review.created_at).toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const title = item.querySelector('[data-review-title]');
    if (review.title) title.textContent = review.title;
    else title.remove();
    item.querySelector('[data-review-text]').textContent = review.content || '';
    item.querySelector('[data-review-author]').textContent = review.author || '';
    item.querySelector('[data-review-verified]').hidden = !review.verified;
    return item;
  }

  async top() {
    const request = ++this.request;
    try {
      const { reviews } = await this.fetchPage(12);
      if (!this.isConnected || request !== this.request) return;
      const best = reviews.filter((review) => review.rating === 5 && review.content).slice(0, 3);
      if (!best.length) return;
      this.list.replaceChildren(...best.map((review) => this.item(review)));
      this.hidden = false;
    } catch {
      /* the rating line still tells */
    }
  }

  async load(more = false) {
    if (more && this.loading) return;
    if (!more) this.offset = 0;
    const offset = this.offset;
    const request = ++this.request;
    this.loading = true;
    try {
      const { reviews, summary, has_more: hasMore } = await this.fetchPage(8);
      if (!this.isConnected || request !== this.request) return;
      if (!more) {
        this.list.replaceChildren();
        this.bars(summary?.rating_histogram);
      }
      const items = reviews.map((review) => this.item(review));
      this.list.append(...items);
      this.offset = offset + reviews.length;
      this.querySelector('[data-review-more-row]').hidden = !hasMore;
      // After «Vis flere» the reading goes on from the first new review, as V3.
      if (more && items[0]) {
        items[0].tabIndex = -1;
        items[0].focus();
      }
    } catch {
      /* the average above still tells */
    } finally {
      if (request === this.request) this.loading = false;
    }
  }

  /** The histogram counts one star first. */
  bars(counts) {
    if (!counts) return;
    const total = counts.reduce((sum, count) => sum + count, 0) || 1;
    this.querySelectorAll('[data-stars]').forEach((bar) => {
      const given = counts[bar.dataset.stars - 1] || 0;
      bar.querySelector('.meter > span').style.width = `${(given / total) * 100}%`;
      bar.querySelector('.chip-count').textContent = given;
    });
    this.querySelector('[data-review-bars]').hidden = false;
  }
}

customElements.define('product-reviews', ProductReviews);

/* <related-products>: «Andre kjøpte også» from Shopify, asked for when the row comes near, so the product loads
   first and the row is in place before a quick scroll reaches the footer. What the page
   shows already (the product, «Fullfør antrekket», the category row) is left out; with fewer than two left, the row
   stays away. */
class RelatedProducts extends HTMLElement {
  connectedCallback() {
    const start = () => {
      this.near.disconnect();
      if (!this.started) this.load();
      this.started = true;
    };
    this.near = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && start(), {
      rootMargin: '600px 0px',
    });
    this.near.observe(this);
  }

  disconnectedCallback() {
    this.near?.disconnect();
  }

  async load() {
    const html = await fetch(this.dataset.url)
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '');
    if (!html || !this.isConnected) {
      this.started = false;
      return;
    }
    const rail = new DOMParser().parseFromString(html, 'text/html').querySelector('.home-section');
    if (!rail) return this.remove();
    const path = (link) => new URL(link.getAttribute('href'), location.href).pathname;
    const links = document.querySelectorAll(
      '[data-category-rail] .card-title, .pdp-upsell .row-title, .matching-set-title',
    );
    const shown = new Set([location.pathname, ...[...links].map(path)]);
    const families = new Set();
    const family = (node) => (node.dataset.productFamily || '').split(',').filter(Boolean);
    document
      .querySelectorAll(
        '[data-product-page], [data-category-rail] [data-product-card], .pdp-upsell [data-product-family], .matching-set[data-product-family]',
      )
      .forEach((node) => family(node).forEach((id) => families.add(id)));
    rail.querySelectorAll('[data-product-card]').forEach((card) => {
      const url = path(card.querySelector('.card-title'));
      const ids = family(card);
      if (shown.has(url) || ids.some((id) => families.has(id))) card.remove();
      else {
        shown.add(url);
        ids.forEach((id) => families.add(id));
      }
    });
    if (rail.querySelectorAll('[data-product-card]').length < 2) return this.remove();
    this.replaceWith(rail);
  }
}

customElements.define('related-products', RelatedProducts);
